// ================================================================
//  EditBizInterceptorDialogController
//  Opens the biz interceptor config wizard in EDIT mode.
//
//  Locals injected:
//    - interceptor : the list row (biz_interceptor_id, ... — same DTO
//                     shape as the full get-by-id response, but possibly
//                     stale). Used as a fallback if the refetch below fails.
//    - profile     : current profile
//
//  Session handling mirrors EditBizEventVersionController: snapshot the
//  original state once loaded (angular.copy), never mutate the caller's
//  row object, recompute "hasChanges" from that snapshot on every edit
//  (not a one-way latch), and only confirm-discard on Cancel if dirty. The
//  save payload itself is diff-only (changed fields only), matching this
//  feature's genuinely-partial PATCH endpoint — unlike biz events' whole-
//  payload PUT.
// ================================================================

angular.module('bizmetryApp').controller('EditBizInterceptorDialogController', function (
    $scope, $q, $timeout, $mdDialog, dialogService,
    BizInterceptorService, ReferenceDataService, ConfigurationService, UserService,
    BusinessEventsService,
    interceptor, profile
) {
    var vm = this;

    vm.discoveryHttpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS', 'TRACE', 'CONNECT'];
    vm.toggleDiscoveryHttpMethod = function (parameters, method) {
        var methods = parameters.discoveryHttpMethods;
        var index = methods.indexOf(method);
        if (index === -1) {
            methods.push(method);
        } else {
            methods.splice(index, 1);
        }
    };

    vm.profile = profile;
    vm.interceptor = interceptor;
    // The account the interceptor belongs to (the Alerting tab lists its users). The row the
    // dialog is opened from carries no account_id — only the full detail (_populateModel) does —
    // so start from the profile's, the same account.
    vm.accountId = (interceptor && interceptor.account_id) || (profile && (profile.accountId || profile.account_id)) || null;
    vm.loading = true;     // while the full detail + refdata + existing names load
    vm.applying = false;   // spinner while the PATCH is in flight

    // Which of the profile's biz events this interceptor should capture —
    // see biz-event-picker/BizEventPicker.js. Populated alongside the rest
    // of the initial load below (vm.bizEvents), consumed by _populateModel.
    vm.bizEvents = [];

    // Per-metric display config for the SLAs tab's Pressure/Agent Latency
    // sub-tabs (real nested <md-tabs>, each with its own copy of the panel
    // markup hardcoded to its own metric key — see
    // edit-biz-interceptor-dialog.html's own comment on that tab). Kept
    // keyed by metric here regardless, since both the HTML's own duplicated
    // copies and vm.clampMonitoringThreshold/clampMonitoringField below all
    // still address a metric by this same string key.
    vm.monitoringMetricMeta = {
        pressure: {
            label: 'Pressure', unit: '%',
            thresholdMin: 0, thresholdMax: 100, thresholdStep: 1,
            description: "How full this interceptor's own capture buffer is (0-100%) — see the Average Pressure badge on Consolidated Stats."
        },
        agentLatency: {
            label: 'Agent Latency', unit: 'ms',
            thresholdMin: 0, thresholdMax: 10000, thresholdStep: 50,
            description: "Round-trip time of this interceptor's own connectivity check to its local Agent — see Avg Ping Time on Consolidated Stats."
        }
    };

    // rzSlider dual-handle range options for the SLA Breach Threshold field —
    // exact same shape/behavior as AgentConfigDialogController's own
    // $scope.sliderOptions[metric].threshold (draggableRange + pushRange so
    // the two handles can't cross, translate() feeds the CLEAR/SET bubble
    // labels). minRange is a flat 1 unit here since, unlike agent's config,
    // there is no per-account configurable minimum gap for these two metrics.
    vm.monitoringSliderOptions = {
        pressure: {
            threshold: {
                floor: 0, ceil: 100, step: 1,
                draggableRange: true, pushRange: true, minRange: 1,
                translate: function (value, sliderId, label) {
                    if (label === 'model') { return '<span class="rz-label-clear">RESET ' + value + '%</span>'; }
                    if (label === 'high') { return '<span class="rz-label-set">SET ' + value + '%</span>'; }
                    return value + '%';
                }
            }
        },
        agentLatency: {
            threshold: {
                floor: 0, ceil: 10000, step: 50,
                draggableRange: true, pushRange: true, minRange: 50,
                translate: function (value, sliderId, label) {
                    if (label === 'model') { return '<span class="rz-label-clear">RESET ' + value + 'ms</span>'; }
                    if (label === 'high') { return '<span class="rz-label-set">SET ' + value + 'ms</span>'; }
                    return value + 'ms';
                }
            }
        }
    };

    vm.model = {
        name: '',
        description: '',
        instrumentationType: 'AUTOMATIC',
        transportType: 'REST_API', // immutable — read-only badge, see TRANSPORT TYPE below
        interceptorTypeId: null,
        sdkTypeId: null,
        enabled: true,
        environments: {},
        // SLAs + Alerting tabs — flat UI shape (thresholdSet/thresholdReset/
        // timeSet/timeReset), translated to/from the API's nested {set:
        // {threshold,time}, reset:{...}} shape in _monitoringMetricFromApi/
        // _monitoringMetricToApi below — same flatten-for-editing,
        // nest-for-the-wire pattern agent-management-microservice's own
        // AgentConfigDialogController uses for the identical concept.
        monitoringConfig: {
            pressure: { enabled: false, alertingEnabled: false, thresholdSet: 80, thresholdReset: 60, timeSet: 5, timeReset: 5 },
            agentLatency: { enabled: false, alertingEnabled: false, thresholdSet: 2000, thresholdReset: 1000, timeSet: 5, timeReset: 5 },
            alertConfiguration: { alertingEnabled: false, alertEnableEmail: false, alertEnableSMS: false, alertUsers: [] }
        }
    };

    // =====================================================================
    // TABS — General / Environments, freely switchable (not a step-gated
    // wizard — Transport Type is still dropped since it's immutable after
    // creation, shown as a read-only toolbar badge instead).
    // =====================================================================

    vm.activeTabIndex = 0;

    // =====================================================================
    // TRANSPORT TYPE — read-only in edit mode, shown as a toolbar badge.
    // =====================================================================

    vm.transportTypeInfo = BizInterceptorService.describeTransport(vm.model.transportType);

    function byDescAsc(a, b) {
        return (a.refObjectDesc || a.refObjectLabel || '').localeCompare(b.refObjectDesc || b.refObjectLabel || '');
    }

    // =====================================================================
    // ENVIRONMENT PARAMETERS — same fine-tuning knobs/limits as Create;
    // only the initial population differs (seeded from the fetched
    // environments[].parameters instead of blank defaults).
    // =====================================================================

    vm.environments = vm.profile.environments || [];

    var CORE_PARAM_KEYS = [
        'bufferMaxSize', 'bufferMaxSizeMb', 'syncBatchSize', 'syncBatchSizeMax', 'syncIntervalSeconds',
        'heartbeatIntervalSeconds', 'transmitWorkerCount', 'transmitWorkerCountMax',
        'backpressureScaleDownThresholdPct', 'backpressureScaleUpThresholdPct', 'reconnectIntervalSeconds',
        'dataRetentionDays'
    ];
    function getConfig(key, fallback) {
        var value = ConfigurationService.getFromCache(key);
        return (value === null || value === undefined) ? fallback : Number(value);
    }

    function _paramLimit(name, fallbackMin, fallbackMax, fallbackDef) {
        var base = 'frontend.biz-interceptor.' + name + '.';
        var min = getConfig(base + 'min', fallbackMin);
        var max = getConfig(base + 'max', fallbackMax);
        var def = getConfig(base + 'default', fallbackDef);
        return { min: min, max: max, def: def, step: Math.max(1, Math.floor((max - min) * 0.01)) };
    }

    vm.limits = {
        bufferMaxSize:            _paramLimit('buffer_max_size',            100, 100000, 10000),
        bufferMaxSizeMb:          _paramLimit('buffer_max_size_mb',           1,   4096,    64),
        syncBatchSize:            _paramLimit('sync_batch_size',             10,  50000,  1024),
        syncBatchSizeMax:         _paramLimit('sync_batch_size_max',         10,  50000, 10240),
        syncIntervalSeconds:      _paramLimit('sync_interval_seconds',        1,    300,    30),
        heartbeatIntervalSeconds: _paramLimit('heartbeat_interval_seconds',   5,    600,    60),
        transmitWorkerCount:      _paramLimit('transmit_worker_count',        1,     64,     4),
        transmitWorkerCountMax:   _paramLimit('transmit_worker_count_max',    1,     64,    16),
        backpressureScaleDownThresholdPct: _paramLimit('backpressure_scale_down_threshold_pct', 0, 100, 20),
        backpressureScaleUpThresholdPct:   _paramLimit('backpressure_scale_up_threshold_pct',   0, 100, 70),
        reconnectIntervalSeconds: _paramLimit('reconnect_interval_seconds',   1,    300,    15),
        dataRetentionDays:        _paramLimit('data_retention_days',          1,     60,    30)
    };

    function _defaultParams(keys) {
        var params = {};
        keys.forEach(function (key) { params[key] = vm.limits[key].def; });
        params.discoveryMode = false;
        params.discoveryHttpMethods = ["GET", "POST", "PUT", "PATCH", "DELETE"];
        return params;
    }

    function _defaultEnvironmentConfig() {
        return {
            enabled: false,
            parameters: _defaultParams(CORE_PARAM_KEYS)
        };
    }

    // Seeds one environment's working config from the API's
    // environments[].parameters entry (snake_case) — falls back to platform
    // defaults for any knob the entry doesn't have. Network config (Connect/
    // Read Timeout, Proxy) isn't part of this anymore — it lives on the
    // linked Agent's own Tuning tab now (see agent-config-dialog.html's
    // "Network Settings" section), not edited here.
    function _envConfigFromApi(apiEntry) {
        if (!apiEntry) { return _defaultEnvironmentConfig(); }
        var p = apiEntry.parameters || {};

        function orLimitDefault(value, key) {
            return (value === null || value === undefined) ? vm.limits[key].def : value;
        }

        return {
            enabled: !!apiEntry.enabled,
            parameters: {
                discoveryMode:           !!p.discovery_mode,
                discoveryHttpMethods: Array.isArray(p.discovery_http_methods) ? p.discovery_http_methods.slice() : ["GET", "POST", "PUT", "PATCH", "DELETE"],
                bufferMaxSize:            orLimitDefault(p.buffer_max_size, 'bufferMaxSize'),
                bufferMaxSizeMb:          orLimitDefault(p.buffer_max_size_mb, 'bufferMaxSizeMb'),
                syncBatchSize:            orLimitDefault(p.sync_batch_size, 'syncBatchSize'),
                syncBatchSizeMax:         orLimitDefault(p.sync_batch_size_max, 'syncBatchSizeMax'),
                syncIntervalSeconds:      orLimitDefault(p.sync_interval_seconds, 'syncIntervalSeconds'),
                heartbeatIntervalSeconds: orLimitDefault(p.heartbeat_interval_seconds, 'heartbeatIntervalSeconds'),
                transmitWorkerCount:      orLimitDefault(p.transmit_worker_count, 'transmitWorkerCount'),
                transmitWorkerCountMax:   orLimitDefault(p.transmit_worker_count_max, 'transmitWorkerCountMax'),
                backpressureScaleDownThresholdPct: orLimitDefault(p.backpressure_scale_down_threshold_pct, 'backpressureScaleDownThresholdPct'),
                backpressureScaleUpThresholdPct:   orLimitDefault(p.backpressure_scale_up_threshold_pct, 'backpressureScaleUpThresholdPct'),
                reconnectIntervalSeconds: orLimitDefault(p.reconnect_interval_seconds, 'reconnectIntervalSeconds'),
                dataRetentionDays:        orLimitDefault(p.data_retention_days, 'dataRetentionDays')
            }
        };
    }

    vm.selectedEnvironment = vm.environments.length ? vm.environments[0] : null;

    vm.selectEnvironment = function (env) {
        vm.selectedEnvironment = env;
    };

    vm.isSelectedEnvironmentDisabled = function () {
        return !!vm.selectedEnvironment && !vm.model.environments[vm.selectedEnvironment.id].enabled;
    };

    vm.batchCapacityLimits = {};
    vm.loadingBatchCapacity = true;
    vm.batchCapacityError = null;
    vm.batchSizeCeiling = function (envId) {
        var cap = vm.batchCapacityLimits[envId];
        return Math.min(vm.limits.syncBatchSizeMax.max, cap == null ? Infinity : cap);
    };
    vm.batchSizeMinimum = function (envId, key) {
        return Math.min(vm.limits[key].min, vm.batchSizeCeiling(envId));
    };
    function loadBatchCapacity() {
        return BizInterceptorService.getBatchCapacityLimits(vm.profile).then(function (limits) {
            vm.batchCapacityLimits = limits;
            Object.keys(vm.model.environments).forEach(function (envId) { vm.onSyncBatchSizeMaxChange(envId); });
        }).catch(function () {
            vm.batchCapacityError = 'Could not verify the linked agent batch limits. Reopen this dialog to retry.';
        }).finally(function () { vm.loadingBatchCapacity = false; });
    }

    vm.syncBatchSizeMax = function (envId) {
        var p = vm.model.environments[envId].parameters;
        return Math.min(vm.limits.syncBatchSize.max, p.bufferMaxSize, p.syncBatchSizeMax, vm.batchSizeCeiling(envId));
    };

    // Initial Transmit Worker Count can never exceed this same environment's
    // own configured Max — same dynamic-ceiling idiom as Sync Batch Size
    // above, just against a sibling field instead of a different one.
    vm.transmitWorkerCountInitialMax = function (envId) {
        return Math.min(vm.limits.transmitWorkerCount.max, vm.model.environments[envId].parameters.transmitWorkerCountMax);
    };

    // Worker Scale-Down Pressure Threshold (P1) can never exceed the
    // current Scale-Up threshold (P2) — same dynamic-ceiling idiom as
    // Initial Transmit Worker Count above.
    vm.backpressureScaleDownThresholdMax = function (envId) {
        return Math.min(vm.limits.backpressureScaleDownThresholdPct.max, vm.model.environments[envId].parameters.backpressureScaleUpThresholdPct);
    };

    vm.clampParam = function (envId, section, key) {
        var limit = vm.limits[key];
        var bucket = vm.model.environments[envId][section];
        var max = limit.max;
        if (key === 'syncBatchSize') { max = vm.syncBatchSizeMax(envId); }
        else if (key === 'syncBatchSizeMax') { max = vm.batchSizeCeiling(envId); }
        else if (key === 'transmitWorkerCount') { max = vm.transmitWorkerCountInitialMax(envId); }
        else if (key === 'backpressureScaleDownThresholdPct') { max = vm.backpressureScaleDownThresholdMax(envId); }
        var value = bucket[key];
        if (typeof value === 'string') { value = parseFloat(value); }
        if (value === null || value === undefined || isNaN(value)) { value = limit.def; }
        else if (value < limit.min) { value = limit.min; }
        else if (value > max) { value = max; }
        bucket[key] = Math.min(value, max);
    };

    vm.onBufferMaxSizeChange = function (envId) {
        var cfg = vm.model.environments[envId];
        if (cfg.parameters.syncBatchSize > vm.syncBatchSizeMax(envId)) {
            cfg.parameters.syncBatchSize = vm.syncBatchSizeMax(envId);
        }
    };

    // If Max Sync Batch Size drops below the current Initial, pull Initial
    // down to match — same idiom as onBufferMaxSizeChange above.
    vm.onSyncBatchSizeMaxChange = function (envId) {
        var cfg = vm.model.environments[envId];
        cfg.parameters.syncBatchSizeMax = Math.min(cfg.parameters.syncBatchSizeMax, vm.batchSizeCeiling(envId));
        if (cfg.parameters.syncBatchSize > vm.syncBatchSizeMax(envId)) {
            cfg.parameters.syncBatchSize = vm.syncBatchSizeMax(envId);
        }
    };

    // If Max Transmit Worker Count drops below the current Initial, pull
    // Initial down to match so the two stay consistent — same idiom as
    // onBufferMaxSizeChange above.
    vm.onTransmitWorkerCountMaxChange = function (envId) {
        var cfg = vm.model.environments[envId];
        if (cfg.parameters.transmitWorkerCount > vm.transmitWorkerCountInitialMax(envId)) {
            cfg.parameters.transmitWorkerCount = vm.transmitWorkerCountInitialMax(envId);
        }
    };

    // If the Scale-Up threshold drops below the current Scale-Down
    // threshold, pull Scale-Down down to match — same idiom as
    // onTransmitWorkerCountMaxChange above.
    vm.onBackpressureScaleUpThresholdChange = function (envId) {
        var cfg = vm.model.environments[envId];
        if (cfg.parameters.backpressureScaleDownThresholdPct > vm.backpressureScaleDownThresholdMax(envId)) {
            cfg.parameters.backpressureScaleDownThresholdPct = vm.backpressureScaleDownThresholdMax(envId);
        }
    };

    vm.resetEnvironmentParameters = function (envId) {
        var cfg = vm.model.environments[envId];
        cfg.parameters = _defaultParams(CORE_PARAM_KEYS);
        vm.onSyncBatchSizeMaxChange(envId);
    };

    // =====================================================================
    // HELPERS
    // =====================================================================

    vm.isManual = function () {
        return vm.model.instrumentationType === 'MANUAL';
    };

    vm.getSelectedInterceptorType = function () {
        if (!vm.model.interceptorTypeId) { return null; }
        return vm.instrumentationTypes.find(function (t) { return t.refId === vm.model.interceptorTypeId; }) || null;
    };

    vm.getSelectedSdkType = function () {
        if (!vm.model.sdkTypeId) { return null; }
        return vm.sdkTypes.find(function (t) { return t.refId === vm.model.sdkTypeId; }) || null;
    };

    // =====================================================================
    // NAME / DESCRIPTION — manual "Regenerate" only, same slug/acronym
    // scheme as Create; never auto-triggered in edit mode.
    // =====================================================================

    var MAX_NAME_LEN = 120;
    var MAX_DESCRIPTION_LEN = 255;

    vm.existingNames = [];       // lowercased biz_interceptor_name values across the whole account, excluding this interceptor
    vm.nameAlreadyExists = false;

    function _slug(str, maxLen) {
        return (str || '')
            .toString().trim()
            .replace(/[^a-zA-Z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .substring(0, maxLen || 40);
    }

    function _acronym(str) {
        return (str || '')
            .toString().trim()
            .split(/\s+/)
            .map(function (word) { return word.charAt(0).toUpperCase(); })
            .join('');
    }

    function _timeSerial() { return Date.now().toString(36); }

    function _selectedTypeLabel() {
        var selected = vm.isManual() ? vm.getSelectedSdkType() : vm.getSelectedInterceptorType();
        return selected ? (selected.refObjectDesc || selected.refObjectLabel || '') : '';
    }

    vm.onNameChange = function () {
        var trimmed = (vm.model.name || '').trim().toLowerCase();
        vm.nameAlreadyExists = trimmed.length > 0 && vm.existingNames.indexOf(trimmed) !== -1;
    };

    vm.onNameEdited = function () { vm.onNameChange(); };

    vm.regenerateName = function () {
        var profileSlug = _slug(vm.profile.profileName || vm.profile.name, 40);
        var typeWord = vm.isManual() ? 'Manual' : 'Automatic';
        var typeLabel = _selectedTypeLabel();
        var serial = _timeSerial();

        function assemble(typePart) {
            return [profileSlug, typeWord, typePart, serial]
                .filter(function (p) { return !!p; })
                .join('-');
        }

        var name = assemble(_slug(typeLabel, 40));
        if (name.length > MAX_NAME_LEN && typeLabel) {
            name = assemble(_acronym(typeLabel));
        }

        vm.model.name = name.substring(0, MAX_NAME_LEN);
        vm.onNameChange();
    };

    vm.regenerateDescription = function () {
        var profileName = vm.profile.profileName || vm.profile.name || '';
        var modeWord = vm.isManual() ? 'manual' : 'automatic';
        var typeLabel = _selectedTypeLabel() || (vm.isManual() ? 'the selected SDK' : 'the selected interceptor type');

        var description = 'Captures API traffic for the ' + profileName + ' profile via '
            + modeWord + ' business instrumentation using ' + typeLabel
            + ', feeding this profile\'s configured biz events.';

        vm.model.description = description.substring(0, MAX_DESCRIPTION_LEN);
    };

    // =====================================================================
    // FORM VALIDATION
    // =====================================================================

    vm.isFormValid = function () {
        if (vm.loadingBatchCapacity || vm.batchCapacityError) { return false; }
        var isNameValid = vm.model.name && vm.model.name.trim() !== '';
        var isTypeValid = vm.isManual() ? !!vm.model.sdkTypeId : !!vm.model.interceptorTypeId;
        return isNameValid && isTypeValid && !vm.nameAlreadyExists;
    };

    // =====================================================================
    // EDIT SESSION — snapshot + recomputed dirty tracking, mirroring
    // EditBizEventVersionController's approach (see file header).
    // =====================================================================

    vm._original = null;

    vm.hasChanges = function () {
        return !!vm._original && !angular.equals(vm.model, vm._original);
    };

    // =====================================================================
    // INITIAL LOAD — refetch the full detail (freshness) in parallel with
    // refdata + existing names; falls back to the passed-in list row if the
    // refetch fails, since that row already carries every field this
    // dialog needs (unlike Edit Biz Event, which genuinely needs a refetch
    // for data the list response omits).
    // =====================================================================

    // ── SLAs/Alerting — API (nested) <-> UI (flat) mapping ──────────────
    // See vm.model.monitoringConfig's own comment for why this split exists.

    function _monitoringMetricFromApi(raw, defaults) {
        raw = raw || {};
        var set = raw.set || {};
        var reset = raw.reset || {};
        return {
            enabled: !!raw.enabled,
            alertingEnabled: !!raw.alerting_enabled,
            thresholdSet: (set.threshold !== null && set.threshold !== undefined) ? set.threshold : defaults.thresholdSet,
            thresholdReset: (reset.threshold !== null && reset.threshold !== undefined) ? reset.threshold : defaults.thresholdReset,
            timeSet: (set.time !== null && set.time !== undefined) ? set.time : defaults.timeSet,
            timeReset: (reset.time !== null && reset.time !== undefined) ? reset.time : defaults.timeReset
        };
    }

    // status/last_health_change_ts are deliberately never sent — they're
    // job-owned (see BizInterceptorServiceImpl's monitoring merge helper,
    // which always preserves them from the persisted row on a PATCH).
    function _monitoringMetricToApi(ui) {
        return {
            enabled: !!ui.enabled,
            alerting_enabled: !!ui.alertingEnabled,
            set: { threshold: ui.thresholdSet, time: ui.timeSet },
            reset: { threshold: ui.thresholdReset, time: ui.timeReset }
        };
    }

    function _monitoringConfigFromApi(raw) {
        var defaults = vm.model.monitoringConfig;
        raw = raw || {};
        var alertConfig = raw.alert_configuration || {};
        return {
            pressure: _monitoringMetricFromApi(raw.pressure, defaults.pressure),
            agentLatency: _monitoringMetricFromApi(raw.agent_latency, defaults.agentLatency),
            alertConfiguration: {
                alertingEnabled: !!alertConfig.alerting_enabled,
                alertEnableEmail: !!alertConfig.alert_enable_email,
                alertEnableSMS: !!alertConfig.alert_enable_sms,
                alertUsers: (alertConfig.alert_users || []).map(function (u) {
                    return { id: u.id, email: u.email, mobile2FANumber: u.mobile_2fa_number || null,
                             userEnabled: u.user_enabled != null ? u.user_enabled : null };
                })
            }
        };
    }

    // ── SLAs — Set/Reset Time field clamping, same style as vm.clampParam
    // above (blank/NaN falls back to a default rather than being left
    // invalid).
    // =====================================================================

    vm.clampMonitoringField = function (metric, field, min, max, def) {
        var bucket = vm.model.monitoringConfig[metric];
        var value = bucket[field];
        if (typeof value === 'string') { value = parseFloat(value); }
        if (value === null || value === undefined || isNaN(value)) { value = def; }
        else if (value < min) { value = min; }
        else if (max !== null && value > max) { value = max; }
        bucket[field] = value;
    };

    // ── SLAs — SLA Breach Threshold dual clamp, identical algorithm to
    // AgentConfigDialogController's own $scope.clampThreshold: keeps the
    // two number inputs beside the rzSlider in sync with it and enforces
    // the slider's own minRange (the handles can't be typed past each
    // other), then forces the rzSlider to re-render so a manually-typed
    // value is reflected on the track immediately.
    // =====================================================================

    vm.clampMonitoringThreshold = function (metric, edited) {
        var cfg = vm.model.monitoringConfig[metric];
        var opts = vm.monitoringSliderOptions[metric].threshold;
        var floor = opts.floor || 0;
        var ceil = opts.ceil;
        var minRange = Math.max(opts.step || 1, opts.minRange || 0);

        var reset = parseFloat(cfg.thresholdReset);
        var set = parseFloat(cfg.thresholdSet);
        if (isNaN(reset)) { reset = floor; }
        if (isNaN(set)) { set = ceil; }

        reset = Math.max(floor, Math.min(reset, ceil));
        set = Math.max(floor, Math.min(set, ceil));

        if (edited === 'set') {
            if (set - reset < minRange) { set = reset + minRange; if (set > ceil) { set = ceil; } }
        } else {
            if (set - reset < minRange) { reset = set - minRange; if (reset < floor) { reset = floor; } }
        }

        cfg.thresholdReset = reset;
        cfg.thresholdSet = set;
        $timeout(function () { $scope.$broadcast('rzSliderForceRender'); }, 0);
    };

    // ── Alerting — recipient picker, same rules and list as
    // AgentConfigDialogController's own (UserService.getAllUsers, filtered
    // client-side, one checkbox per user). Eligible: confirmed users (they
    // completed the sign-up) and, while SMS is on, only those with a 2FA
    // mobile number — an SMS can't reach anyone else. Loaded once, lazily,
    // the first time the Alerting tab is actually opened.
    // =====================================================================

    vm.alertUserSearch = { query: '', displayList: [], loading: false, allUsers: [] };
    var _alertUsersLoaded = false;

    vm.ensureAlertUsersLoaded = function () {
        if (_alertUsersLoaded || vm.alertUserSearch.loading) { return; }
        if (!vm.accountId) {
            console.error('[EditBizInterceptorDialog] No account id to load alert recipients for');
            return;
        }
        _alertUsersLoaded = true;
        vm.alertUserSearch.loading = true;
        UserService.getAllUsers(vm.accountId, { page: 0, size: 1000 })
            .then(function (r) {
                var content = (r.data && r.data.content) || r.data || [];
                vm.alertUserSearch.allUsers = content;
                _purgeIneligibleRecipients();
                vm.onAlertUserSearchInput();
            })
            .catch(function (err) {
                console.error('[EditBizInterceptorDialog] Error loading users for alert recipients:', err);
                _alertUsersLoaded = false;  // retried the next time the tab is opened
                vm.alertUserSearch.allUsers = [];
                vm.alertUserSearch.displayList = [];
            })
            .finally(function () { vm.alertUserSearch.loading = false; });
    };

    function _smsOn() {
        return !!(vm.model.monitoringConfig && vm.model.monitoringConfig.alertConfiguration
            && vm.model.monitoringConfig.alertConfiguration.alertEnableSMS);
    }

    function _has2FA(number) { return !!(number && number.trim()); }

    // An account user who can be a recipient: confirmed, and with a 2FA number while SMS is on.
    // 2FA on: the account's flag (user2FAEnabled) and a registered number.
    function _userHas2FA(user) { return user.user2FAEnabled !== false && _has2FA(user.user2FANumber); }

    function _eligible(user) {
        return user.isConfirmed === true && (!_smsOn() || _userHas2FA(user));
    }

    // Selected recipients that stopped being eligible leave the selection. One no longer in the
    // account (e.g. deleted) stays — listed, so it can be unchecked — unless SMS needs a number.
    function _purgeIneligibleRecipients() {
        if (!vm.alertUserSearch.allUsers.length) { return; }
        var byId = {};
        vm.alertUserSearch.allUsers.forEach(function (u) { byId[u.id] = u; });
        var ac = vm.model.monitoringConfig.alertConfiguration;
        ac.alertUsers = ac.alertUsers.filter(function (r) {
            return byId[r.id] ? _eligible(byId[r.id]) : (!_smsOn() || _has2FA(r.mobile2FANumber));
        });
    }

    vm.onAlertUserSearchInput = function () {
        var q = (vm.alertUserSearch.query || '').toLowerCase().trim();
        var known = {};
        vm.alertUserSearch.allUsers.forEach(function (u) { known[u.id] = true; });
        var users = vm.alertUserSearch.allUsers.filter(_eligible).concat(
            vm.model.monitoringConfig.alertConfiguration.alertUsers
                .filter(function (r) { return !known[r.id]; })
                .map(function (r) { return { id: r.id, email: r.email, user2FANumber: r.mobile2FANumber }; }));
        users = !q ? users : users.filter(function (u) {
            return (u.email || '').toLowerCase().indexOf(q) > -1 ||
                (u.firstName || '').toLowerCase().indexOf(q) > -1 ||
                (u.lastName || '').toLowerCase().indexOf(q) > -1;
        });
        // Selected recipients first, so it's clear at a glance who's picked.
        users.sort(function (a, b) {
            var aAdded = vm.isAlertUserAdded(a.id), bAdded = vm.isAlertUserAdded(b.id);
            if (aAdded !== bAdded) { return aAdded ? -1 : 1; }
            return (a.email || '').localeCompare(b.email || '');
        });
        vm.alertUserSearch.displayList = users;
    };

    // SMS on: only users with a 2FA number; the selected ones without it leave the selection.
    $scope.$watch(_smsOn, function (smsOn, was) {
        if (smsOn === was || !vm.alertUserSearch.allUsers.length) { return; }
        if (smsOn) { _purgeIneligibleRecipients(); }
        vm.onAlertUserSearchInput();
    });

    // A recipient's current enabled flag — the freshly loaded account user's when known (the
    // backend keeps it in step via UserChangedEvent too). A disabled recipient stays checked, but
    // gets no alerts while disabled.
    function _recipientEnabled(recipient) {
        var known = vm.alertUserSearch.allUsers.filter(function (u) { return u.id === recipient.id; })[0];
        var enabled = known ? known.userEnabled : recipient.userEnabled;
        return enabled != null ? enabled : null;
    }

    vm.mask2FA = function (number) {
        return _has2FA(number) ? '****' + number.trim().slice(-4) : null;
    };

    vm.isAlertUserAdded = function (userId) {
        return vm.model.monitoringConfig.alertConfiguration.alertUsers.some(function (u) { return u.id === userId; });
    };

    vm.toggleAlertUser = function (user) {
        var list = vm.model.monitoringConfig.alertConfiguration.alertUsers;
        var idx = -1;
        list.forEach(function (u, i) { if (u.id === user.id) { idx = i; } });
        if (idx > -1) { list.splice(idx, 1); }
        else { list.push({ id: user.id, email: user.email, mobile2FANumber: user.user2FANumber || null,
                           userEnabled: user.userEnabled != null ? user.userEnabled : null }); }
    };

    function _populateModel(detail) {
        vm.accountId = detail.account_id || vm.accountId;
        vm.model.name = detail.biz_interceptor_name || '';
        vm.model.description = detail.biz_interceptor_description || '';
        vm.model.instrumentationType = detail.instrumentation_type || 'AUTOMATIC';
        vm.model.transportType = detail.transport_type || 'REST_API';
        vm.model.enabled = detail.enabled !== false;
        vm.model.monitoringConfig = _monitoringConfigFromApi(detail.monitoring_config);

        vm.model.interceptorTypeId = null;
        vm.model.sdkTypeId = null;
        if (detail.interceptor_type && detail.interceptor_type.id) {
            if (vm.isManual()) { vm.model.sdkTypeId = detail.interceptor_type.id; }
            else { vm.model.interceptorTypeId = detail.interceptor_type.id; }
        }

        var byEnvId = {};
        (detail.environments || []).forEach(function (e) { byEnvId[e.id] = e.parameters; });
        vm.model.environments = {};
        vm.environments.forEach(function (env) {
            vm.model.environments[env.id] = _envConfigFromApi(byEnvId[env.id]);
        });

        vm.transportTypeInfo = BizInterceptorService.describeTransport(vm.model.transportType);

        // null (predates this field) -> fall back to "every biz event of
        // the profile", the accurate picture of what this interceptor
        // actually captures today (see BizInterceptor#bizEventIds's
        // null-means-unrestricted contract). Requires vm.bizEvents already
        // resolved — see the $q.all load below.
        vm.model.bizEventIds = detail.biz_event_ids
            ? detail.biz_event_ids.filter(function (id) {
                return vm.bizEvents.some(function (event) { return event.biz_event_id === id; });
            })
            : vm.bizEvents.map(function (e) { return e.biz_event_id; });
    }

    $q.all({
        detail: BizInterceptorService.getById(interceptor.biz_interceptor_id)
            .then(function (r) { return r.data; })
            .catch(function (err) {
                console.error('[EditBizInterceptorDialog] Error fetching interceptor detail:', err);
                return null;
            }),
        instrumentationTypes: ReferenceDataService.getReferenceData('BIZ_INSTRUMENTATION_TYPE')
            .then(function (r) { return r.data || []; })
            .catch(function () { return []; }),
        sdkTypes: ReferenceDataService.getReferenceData('INTERCEPTOR_SDK_TYPE')
            .then(function (r) { return r.data || []; })
            .catch(function () { return []; }),
        // profile_id omitted on purpose — the backend's uniqueness
        // constraint (BizInterceptorServiceImpl#patch) is
        // existsByBizInterceptorNameAndAccountIdAndBizInterceptorIdNot,
        // account-wide rather than per-profile, so this pre-check has to
        // match that scope or a name colliding with an interceptor in a
        // different profile of the same account would pass here and only
        // fail on save.
        existing: BizInterceptorService.listByProfile(null, { size: 1000 })
            .then(function (r) { return (r.data && r.data.content) || []; })
            .catch(function () { return []; }),
        bizEvents: BusinessEventsService.getAllForProfile(profile.id)
            .catch(function (err) {
                console.error('[EditBizInterceptorDialog] Error loading biz events:', err);
                return [];
            })
    }).then(function (results) {
        if (!results.detail) {
            $mdDialog.cancel();
            dialogService.showErrorDialog('Could not load interceptor', 'The interceptor details could not be loaded. Please retry before editing.');
            return;
        }
        vm.instrumentationTypes = results.instrumentationTypes.sort(byDescAsc);
        vm.sdkTypes = results.sdkTypes.sort(byDescAsc);

        vm.existingNames = results.existing
            .filter(function (i) { return i.biz_interceptor_id !== interceptor.biz_interceptor_id; })
            .map(function (i) { return (i.biz_interceptor_name || '').trim().toLowerCase(); });

        vm.bizEvents = BusinessEventsService.filterByTransport(results.bizEvents || [], results.detail.transport_type || 'REST_API');

        _populateModel(results.detail);
        vm.onNameChange();

        // Registered only now (not at controller construction) — watching
        // from the start would see the model's initial 'AUTOMATIC' default
        // transition to whatever _populateModel just loaded (e.g. 'MANUAL')
        // as if the user had changed it, and immediately null out the
        // sdkTypeId/interceptorTypeId this same function just set. From
        // here on it only reacts to genuine user-driven changes (Unlike
        // Create, edit does NOT auto-regenerate name/description on type
        // change — silently renaming an existing interceptor would be
        // surprising; Regenerate is still available as an explicit button).
        $scope.$watch('vm.model.instrumentationType', function (newVal, oldVal) {
            if (newVal === oldVal) { return; }
            vm.model.interceptorTypeId = null;
            vm.model.sdkTypeId = null;
        });

        vm._original = angular.copy(vm.model);
        loadBatchCapacity();
        vm.loading = false;
    });

    // =====================================================================
    // CANCEL — confirm discard only if dirty, same as Edit Biz Event.
    // =====================================================================

    vm.cancel = function () {
        if (!vm.hasChanges()) { $mdDialog.cancel(); return; }
        dialogService.showDecisionDialog(
            'Discard Changes',
            'You have unsaved changes. Are you sure you want to close without applying them?'
        ).then(function (decision) {
            if (decision === 'proceed') { $mdDialog.cancel(); }
        });
    };

    // =====================================================================
    // APPLY CHANGES — diff-only PATCH payload (only fields that actually
    // changed vs. vm._original), unlike Edit Biz Event's whole-payload PUT.
    // =====================================================================

    vm.applyChanges = function () {
        if (vm.applying || !vm.hasChanges() || !vm.isFormValid()) { return; }

        vm.applying = true;

        var nameTrimmed = (vm.model.name || '').trim();
        var descTrimmed = (vm.model.description || '').trim();
        var payload = {};

        if (nameTrimmed !== vm._original.name) { payload.biz_interceptor_name = nameTrimmed; }
        if (descTrimmed !== vm._original.description) { payload.biz_interceptor_description = descTrimmed; }

        if (vm.model.instrumentationType !== vm._original.instrumentationType ||
            vm.model.interceptorTypeId !== vm._original.interceptorTypeId ||
            vm.model.sdkTypeId !== vm._original.sdkTypeId) {
            var selectedType = vm.isManual() ? vm.getSelectedSdkType() : vm.getSelectedInterceptorType();
            payload.instrumentation_type = vm.model.instrumentationType;
            // "name" carries the stable ref_object_label (the backend
            // discriminates by it); "description" is the human-readable
            // text, stored too so list/detail views never need to re-
            // resolve it against the reference-data catalog.
            payload.interceptor_type = selectedType
                ? { id: selectedType.refId, name: selectedType.refObjectLabel, description: selectedType.refObjectDesc || selectedType.refObjectLabel }
                : null;
        }

        if (vm.model.enabled !== vm._original.enabled) { payload.enabled = vm.model.enabled; }

        // environments is all-or-nothing server-side (see
        // BizInterceptorServiceImpl#buildEnvironmentParameters) — resend
        // every profile environment, not just the one(s) actually edited.
        if (!angular.equals(vm.model.environments, vm._original.environments)) {
            payload.environments = vm.environments.map(function (env) {
                var cfg = vm.model.environments[env.id];
                return {
                    id: env.id,
                    enabled: !!cfg.enabled,
                    parameters: {
                        discovery_mode:             !!cfg.parameters.discoveryMode,
                        discovery_http_methods: cfg.parameters.discoveryHttpMethods.slice(),
                        buffer_max_size:            cfg.parameters.bufferMaxSize,
                        buffer_max_size_mb:         cfg.parameters.bufferMaxSizeMb,
                        sync_batch_size:            cfg.parameters.syncBatchSize,
                        sync_batch_size_max:        cfg.parameters.syncBatchSizeMax,
                        sync_interval_seconds:      cfg.parameters.syncIntervalSeconds,
                        heartbeat_interval_seconds: cfg.parameters.heartbeatIntervalSeconds,
                        transmit_worker_count:      cfg.parameters.transmitWorkerCount,
                        transmit_worker_count_max:  cfg.parameters.transmitWorkerCountMax,
                        backpressure_scale_down_threshold_pct: cfg.parameters.backpressureScaleDownThresholdPct,
                        backpressure_scale_up_threshold_pct:   cfg.parameters.backpressureScaleUpThresholdPct,
                        reconnect_interval_seconds: cfg.parameters.reconnectIntervalSeconds,
                        data_retention_days:        cfg.parameters.dataRetentionDays
                    }
                };
            });
        }

        // Same "full block replace when present" contract as environments
        // above — see PatchBizInterceptorDTO#monitoringConfig's own doc.
        if (!angular.equals(vm.model.monitoringConfig, vm._original.monitoringConfig)) {
            var mc = vm.model.monitoringConfig;
            payload.monitoring_config = {
                pressure: _monitoringMetricToApi(mc.pressure),
                agent_latency: _monitoringMetricToApi(mc.agentLatency),
                alert_configuration: {
                    alerting_enabled: !!mc.alertConfiguration.alertingEnabled,
                    alert_enable_email: !!mc.alertConfiguration.alertEnableEmail,
                    alert_enable_sms: !!mc.alertConfiguration.alertEnableSMS,
                    alert_users: mc.alertConfiguration.alertUsers.map(function (u) {
                        return { id: u.id, email: u.email, mobile_2fa_number: u.mobile2FANumber || null,
                                 user_enabled: _recipientEnabled(u) };
                    })
                }
            };
        }

        // Same "full block replace when present" contract as environments/
        // monitoring_config above. Untouched (still equal to the snapshot,
        // including the synthesized "all" fallback _populateModel seeds for
        // a legacy null interceptor — see there) means this key is omitted
        // entirely, which is what keeps a legacy interceptor's bizEventIds
        // null (unrestricted) until someone actually changes the selection.
        if (!angular.equals(vm.model.bizEventIds, vm._original.bizEventIds)) {
            payload.biz_event_ids = vm.model.bizEventIds.slice();
        }

        if (Object.keys(payload).length === 0) { vm.applying = false; return; }

        BizInterceptorService.patch(vm.interceptor.biz_interceptor_id, payload)
            .then(function () {
                $mdDialog.hide({ updated: true });
            })
            .catch(function (err) {
                vm.applying = false;
                console.error('Error updating biz interceptor:', err);
                var errorMessage = 'An error occurred while updating the biz interceptor.';
                if (err.data && err.data.message) { errorMessage = err.data.message; }
                else if (err.statusText) { errorMessage = err.statusText; }
                dialogService.showErrorDialog('Update Failed', errorMessage);
            });
    };

});
